// Engagement Intelligence Console — full visibility into the Aris engagement
// engine: every outbound message, delivery/open/click, and which messages drove
// sessions & conversions, down to an individual user's message history.
//
// One route, eight dashboards as sub-tabs (contract build order). A single
// range filter (presets + custom start/end) lives in the toolbar and feeds the
// six time-windowed dashboards; the User drill-down owns its own search/paging.
// Source: core-lambda admin_module/engagement_console.

import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../ui/tabs";
import {
  CustomRangePopover,
  DashboardSurface,
  Toolbar,
} from "../../../Components/admin/reporting/controls";
import OverviewTab from "./OverviewTab";
import UserDrillDownTab from "./UserDrillDownTab";
import MessageTab from "./MessageTab";
import ChannelTab from "./ChannelTab";
import SegmentTab from "./SegmentTab";
import SendTimeTab from "./SendTimeTab";
import FunnelTab from "./FunnelTab";
import AttributionTab from "./AttributionTab";

// Contract §2 range presets.
const RANGE_PRESETS = [
  ["today", "Today"],
  ["yesterday", "Yesterday"],
  ["7d", "Last 7 Days"],
  ["30d", "Last 30 Days"],
  ["60d", "Last 60 Days"],
  ["90d", "Last 90 Days"],
  ["ytd", "Year to Date"],
  ["lifetime", "Lifetime"],
];

// Preset <select> + custom-range popover. Emits either a preset string or a
// {start,end} object via onChange; custom range wins while active.
const RangeFilter = ({ preset, custom, onPreset, onCustom, onClearCustom }) => (
  <Toolbar>
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Range
      </span>
      <select
        value={custom ? "" : preset}
        onChange={(e) => onPreset(e.target.value)}
        className="rounded-md border border-slate-300 bg-background px-2 py-1 text-xs font-medium shadow-sm focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-300"
      >
        {custom ? <option value="">Custom</option> : null}
        {RANGE_PRESETS.map(([k, l]) => (
          <option key={k} value={k}>
            {l}
          </option>
        ))}
      </select>
    </div>
    <CustomRangePopover active={custom} onApply={onCustom} onClear={onClearCustom} />
  </Toolbar>
);

const AdminEngagement = () => {
  const [tab, setTab] = useState("overview");
  const [preset, setPreset] = useState("30d");
  const [custom, setCustom] = useState(null); // {start,end} | null

  // What every windowed tab receives: the custom range if set, else the preset.
  const range = custom || preset;

  // The two user endpoints take no range — hide the filter on that tab.
  const showRange = tab !== "users";

  return (
    <DashboardSurface>
      <div className="space-y-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">
              Engagement Intelligence
            </h1>
            <p className="text-sm text-muted-foreground">
              Every outbound message, its delivery &amp; opens, and the sessions
              and conversions it drove.
            </p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <TabsList className="flex-wrap">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="users">User Drill-Down</TabsTrigger>
              <TabsTrigger value="messages">Messages</TabsTrigger>
              <TabsTrigger value="channels">Channels</TabsTrigger>
              <TabsTrigger value="segments">Segments</TabsTrigger>
              <TabsTrigger value="send-time">Send-Time</TabsTrigger>
              <TabsTrigger value="funnel">Funnel</TabsTrigger>
              <TabsTrigger value="attribution">Attribution</TabsTrigger>
            </TabsList>
            {showRange ? (
              <RangeFilter
                preset={preset}
                custom={custom}
                onPreset={(p) => {
                  setCustom(null);
                  setPreset(p);
                }}
                onCustom={(r) => setCustom(r)}
                onClearCustom={() => setCustom(null)}
              />
            ) : null}
          </div>

          <TabsContent value="overview" className="mt-0">
            <OverviewTab range={range} />
          </TabsContent>
          <TabsContent value="users" className="mt-0">
            <UserDrillDownTab />
          </TabsContent>
          <TabsContent value="messages" className="mt-0">
            <MessageTab range={range} />
          </TabsContent>
          <TabsContent value="channels" className="mt-0">
            <ChannelTab range={range} />
          </TabsContent>
          <TabsContent value="segments" className="mt-0">
            <SegmentTab range={range} />
          </TabsContent>
          <TabsContent value="send-time" className="mt-0">
            <SendTimeTab range={range} />
          </TabsContent>
          <TabsContent value="funnel" className="mt-0">
            <FunnelTab range={range} />
          </TabsContent>
          <TabsContent value="attribution" className="mt-0">
            <AttributionTab range={range} />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardSurface>
  );
};

export default AdminEngagement;
